import { Injectable, Injector } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { AuthService } from "@modules/auth";
import { ToastrService } from "ngx-toastr";
import { AppStateService } from "../store/app-state.service";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);

  constructor(
    private injector: Injector,
    private appStateService: AppStateService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let request = req;

    const token = this.appStateService.state.currentUser?.jwToken;

    if (request.url.includes(environment.sharpenApi)) {
      request = request.clone({
        setHeaders: { Authorization: `Api-Key ${environment.sharpenApiKey}` },
      });
    } else if (token) {
      request = this.addToken(request, token);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          const toastr = this.injector.get(ToastrService);
          toastr.error(error.error?.Message || "Bad request");
        }

        const isAuthEndpoint =
          request.url.includes("/authenticate") ||
          request.url.includes("/register") ||
          request.url.includes("/refresh-token");

        if ((error.status === 401 || error.status === 403) && !isAuthEndpoint) {
          return this.refreshAndRetry(request, next, error);
        }

        return throwError(() => error);
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        "Content-Type": "application/json; charset=utf-8",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private refreshAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(request, token!)))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const authService = this.injector.get(AuthService);

    return authService.refreshAuthToken().pipe(
      switchMap((res) => {
        this.isRefreshing = false;

        if (!res?.succeeded) {
          authService.logout();
          return throwError(() => new Error("Token refresh failed"));
        }

        const newToken = res.data.jwToken;
        this.refreshTokenSubject.next(newToken);

        return next.handle(this.addToken(request, newToken));
      }),

      catchError((err) => {
        this.isRefreshing = false;
        this.injector.get(AuthService).logout();
        return throwError(() => err);
      })
    );
  }
}
